/*
 * 14Agency Free Gift v2.0.0 - adapter for the supplied Horizon 3.2.1 sources.
 * Requires cart-gift-eligibility.liquid, free-gift-teaser.liquid and free-gift.css v2.
 * No fetch monkeypatch, no full-cart HTML scrape, no global MutationObserver.
 * URL hotfix: render eligibility and cart Sections at the verified storefront root.
 * Price enforcement at checkout remains the responsibility of Shopify discounts.
 */
const AG_VERSION = '2.0.0';
const AG_SOURCE = '14agency-free-gift-v2';
const AG_SECTION = 'cart-gift-eligibility';
const AG_STATE = '_ag_free_gift_state';

// ---------- Independent information UI (no cart requests or theme imports) ----------
let infoSequence = 0;
class AGGiftInfo extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-ag-gift-info]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      const { panel } = this.parts();
      if (!panel) return;
      if (this.isOpen(panel)) this.close(true);
      else this.open();
    }, options);
    this.addEventListener('toggle', event => {
      const { button, panel } = this.parts();
      if (event.target !== panel) return;
      button?.setAttribute('aria-expanded', String(this.isOpen(panel)));
    }, { ...options, capture: true });
    // Escape closes the information first, not Horizon's enclosing drawer.
    window.addEventListener('keydown', event => {
      const { panel } = this.parts();
      if (event.key !== 'Escape' || !panel || !this.isOpen(panel)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.close(true);
    }, { ...options, capture: true });
    document.addEventListener('click', event => {
      const { panel } = this.parts();
      if (panel?.classList.contains('ag-info-fallback-open') && !this.contains(event.target)) this.close();
    }, options);
    const reposition = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.position(); });
    };
    document.addEventListener('scroll', reposition, { ...options, capture: true, passive: true });
    window.addEventListener('resize', reposition, options);
    window.visualViewport?.addEventListener('resize', reposition, options);
    this.parts();
  }
  disconnectedCallback() {
    this.abort?.abort();
    cancelAnimationFrame(this.frame || 0);
    this.frame = 0;
  }
  parts() {
    const button = this.querySelector('[data-ag-gift-info]');
    const panel = this.querySelector('[data-ag-popover]');
    if (button && panel) {
      if (!panel.id) panel.id = `ag-gift-info-${++infoSequence}`;
      button.setAttribute('aria-controls', panel.id);
    }
    return { button, panel };
  }
  isOpen(panel) {
    if (panel.classList.contains('ag-info-fallback-open')) return true;
    return typeof panel.showPopover === 'function' && panel.matches(':popover-open');
  }
  open() {
    const { button, panel } = this.parts();
    if (!button || !panel) return;
    if (typeof panel.showPopover === 'function') {
      panel.showPopover();
      this.position();
    } else {
      // Keep the disclosure INSIDE the modal, in the card's flex layout.
      panel.classList.add('ag-info-fallback-open');
    }
    button.setAttribute('aria-expanded', 'true');
  }
  close(focus = false) {
    const { button, panel } = this.parts();
    if (!panel) return;
    if (typeof panel.hidePopover === 'function' && panel.matches(':popover-open')) panel.hidePopover();
    panel.classList.remove('ag-info-fallback-open');
    button?.setAttribute('aria-expanded', 'false');
    if (focus) button?.focus({ preventScroll: true });
  }
  position() {
    const { button, panel } = this.parts();
    if (!button || !panel || !this.isOpen(panel) || panel.classList.contains('ag-info-fallback-open')) return;
    if (!button.getClientRects().length) { this.close(); return; }
    const viewport = window.visualViewport;
    const minX = (viewport?.offsetLeft || 0) + 12;
    const minY = (viewport?.offsetTop || 0) + 12;
    const maxX = minX + (viewport?.width || window.innerWidth) - 24;
    const maxY = minY + (viewport?.height || window.innerHeight) - 24;
    const width = Math.max(120, Math.min(280, maxX - minX));
    panel.style.width = `${width}px`;
    panel.style.maxHeight = `${Math.max(80, maxY - minY)}px`;
    const rect = button.getBoundingClientRect();
    const height = panel.getBoundingClientRect().height;
    const left = Math.max(minX, Math.min(maxX - width, rect.right - width));
    const below = rect.bottom + 8;
    const top = below + height <= maxY ? below : Math.max(minY, rect.top - height - 8);
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  }
}
if (!customElements.get('ag-gift-info')) customElements.define('ag-gift-info', AGGiftInfo);

// ---------- Small deterministic state machine ----------
class AGStaleRead extends Error {}
const managed = snapshot => snapshot.items.filter(item => item.managed);
const totalQuantity = items => items.reduce((sum, item) => sum + item.quantity, 0);
const isFree = item => item.quantity > 0 && item.originalLinePrice > 0 && item.netLinePrice === 0;
function fingerprint(snapshot) {
  // Do not include mutable line keys or gift-generated discounts.
  return JSON.stringify([snapshot.variantId, snapshot.collectionId, snapshot.thresholdCents, snapshot.currency,
    snapshot.items.filter(item => !item.managed).map(item =>
      [item.variantId, item.quantity, item.originalLinePrice, item.subscription]).sort()]);
}
function qualified(snapshot) {
  return snapshot.enabled && snapshot.ready && snapshot.available && snapshot.currencySupported &&
    snapshot.qualifiedAmount >= snapshot.thresholdCents;
}
function validateSnapshot(data) {
  if (!data || data.version !== AG_VERSION || !Array.isArray(data.items)) {
    throw new Error('DATA_VERSION: Bitte die Liquid-Section und JavaScript gemeinsam aktualisieren.');
  }
  for (const key of ['enabled', 'ready', 'available', 'currencySupported']) {
    if (typeof data[key] !== 'boolean') throw new Error(`DATA_BOOLEAN: ${key}`);
  }
  for (const key of ['variantId', 'collectionId', 'thresholdCents', 'qualifiedAmount', 'physicalQuantity', 'itemCount', 'cartTotal']) {
    if (!Number.isSafeInteger(data[key]) || data[key] < 0) throw new Error(`DATA_NUMBER: ${key}`);
  }
  if (typeof data.currency !== 'string' || typeof data.giftState !== 'string') throw new Error('DATA_STATE');
  for (const item of data.items) {
    if (typeof item.key !== 'string' || !item.key || typeof item.managed !== 'boolean') throw new Error('DATA_LINE');
    for (const key of ['variantId', 'quantity', 'originalLinePrice', 'lineFinalPrice', 'netLinePrice']) {
      if (!Number.isSafeInteger(item[key]) || item[key] < 0) throw new Error(`DATA_LINE_NUMBER: ${key}`);
    }
  }
  return data;
}

class MarcelPaaFreeGift {
  constructor(events) {
    this.version = AG_VERSION;
    this.events = events;
    this.root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    this.processing = false;
    this.queued = false;
    this.writing = false;
    this.timer = null;
    this.epoch = 0;
    this.nativeQuietUntil = 0;
    this.intent = null;
    this.blocked = null;
    this.snapshot = null;
    this.lastError = null;
    this.checkoutHold = false;
    this.log = [];
    this.abort = new AbortController();
    this.lockedNodes = new Map();
    this.formatters = new Map();
    this.stats = { reads: 0, writes: 0, runs: 0, publishes: 0 };
    this.start();
  }
  trace(stage, details = {}) {
    this.log.push({ time: new Date().toISOString(), stage, ...details });
    if (this.log.length > 150) this.log.shift();
  }
  report() {
    // Deliberately no cart tokens, addresses, notes or complete attributes.
    return JSON.parse(JSON.stringify({ version: this.version, processing: this.processing, writing: this.writing,
      stats: this.stats, lastError: this.lastError, checkoutHold: this.checkoutHold, blocked: this.blocked?.code || null,
      snapshot: this.snapshot, log: this.log }));
  }
  destroy() {
    this.abort.abort();
    clearTimeout(this.timer);
    this.unlock();
  }
  url(path) { return `${this.root}${path.replace(/^\//, '')}`; }
  sectionIds() {
    const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map(element => element.dataset.sectionId).filter(Boolean))];
    if (ids.length > 4) throw new Error('SECTION_LIMIT: Mehr als vier Warenkorb-Sections.');
    return ids;
  }
  async fetchJSON(url, options = {}) {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 12000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: abort.signal });
      let data;
      try { data = await response.json(); }
      catch { throw new Error(`JSON_INVALID: HTTP ${response.status}`); }
      if (!response.ok || data?.errors || Number(data?.status) >= 400) {
        const description = data?.description || data?.message || data?.errors || '';
        throw new Error(`CART_HTTP_${response.status}: ${typeof description === 'string' ? description : JSON.stringify(description)}`);
      }
      return data;
    } finally { clearTimeout(timeout); }
  }
  parseSection(html) {
    if (typeof html !== 'string') throw new Error('SECTION_MISSING: cart-gift-eligibility');
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const node = documentFragment.querySelector('script[data-ag-eligibility]');
    if (!node) throw new Error('SECTION_PAYLOAD: cart-gift-eligibility');
    return validateSnapshot(JSON.parse(node.textContent));
  }
  async read(includeUI = false) {
    const ids = [AG_SECTION, ...(includeUI ? this.sectionIds() : [])];
    // A known working rendering context for the data section: storefront root.
    // Avoid rendering cart-gift-eligibility in arbitrary collection/page templates.
    const url = new URL(this.root, window.location.origin);
    // Carry an explicit theme preview ID when the preview URL contains one.
    const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    url.searchParams.set('sections', ids.join(','));
    this.stats.reads++;
    this.trace('READ', { includeUI });
    const sections = await this.fetchJSON(url.toString(), { headers: { Accept: 'application/json' } });
    return { snapshot: this.parseSection(sections[AG_SECTION]), sections };
  }
  async post(path, payload) {
    this.stats.writes++;
    this.trace('WRITE', { path, quantity: payload.quantity, intent: this.intent });
    const ids = [AG_SECTION, ...this.sectionIds()];
    const response = await this.fetchJSON(this.url(path), {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: ids.join(','), sections_url: this.root }),
    });
    try {
      return { snapshot: this.parseSection(response.sections?.[AG_SECTION]), sections: response.sections,
        resource: Number.isInteger(response.item_count) && Array.isArray(response.items) ? response : null };
    } catch (error) {
      // Mutation may have succeeded even if Section Rendering failed. Never repeat an add blindly.
      this.trace('SECTION_RECOVERY', { message: error.message });
      const recovered = await this.read(true);
      return recovered;
    }
  }
  start() {
    const options = { capture: true, signal: this.abort.signal };
    document.addEventListener(this.events.ThemeEvents.cartUpdate, event => {
      if (event.detail?.sourceId === AG_SOURCE || event.detail?.data?.source === AG_SOURCE) return;
      this.epoch++;
      this.nativeQuietUntil = Date.now() + 100;
      this.trace('NATIVE_CART_UPDATE');
      this.schedule(140);
    }, options);
    document.addEventListener(this.events.ThemeEvents.discountUpdate, () => {
      this.blocked = null;
      this.epoch++;
      this.nativeQuietUntil = Date.now() + 350;
      this.trace('NATIVE_DISCOUNT_UPDATE');
      this.schedule(380);
    }, options);
    document.addEventListener(this.events.ThemeEvents.quantitySelectorUpdate, event => {
      const row = event.target instanceof Element ? event.target.closest('[data-key]') : null;
      const own = this.snapshot?.items.find(item => item.key === row?.dataset.key && item.managed);
      if (own) {
        event.stopImmediatePropagation();
        if (event.detail?.quantity === 0) this.choose('decline');
        return;
      }
      if (!row) return;
      if (this.writing) { event.stopImmediatePropagation(); return; }
      this.epoch++;
      this.nativeQuietUntil = Date.now() + 450; // Horizon's 300 ms input debounce.
    }, options);
    document.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const choice = event.target.closest('[data-ag-gift-action]');
      if (choice) { event.preventDefault(); this.choose(choice.dataset.agGiftAction); return; }
      const remove = event.target.closest('.cart-items__remove');
      if (!remove) return;
      const row = remove.closest('[data-key]');
      const own = this.snapshot?.items.find(item => item.key === row?.dataset.key && item.managed);
      if (own) {
        // Explicit human removal, not inference from a temporarily absent line.
        event.preventDefault();
        event.stopImmediatePropagation();
        this.choose('decline');
      } else {
        this.epoch++;
        this.nativeQuietUntil = Date.now() + 100;
      }
    }, options);
    window.addEventListener('pageshow', () => this.schedule(100), { signal: this.abort.signal });
    this.schedule(100);
  }
  choose(action) {
    if (!['decline', 'restore', 'retry'].includes(action)) return;
    this.intent = action;
    if (action !== 'decline') this.blocked = null;
    this.trace('CHOICE', { action });
    this.schedule(0);
  }
  schedule(delay = 140) {
    if (this.abort.signal.aborted) return;
    if (this.processing) { this.queued = true; return; }
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.timer = null; void this.run(); }, delay);
  }
  async waitForNativeIdle() {
    const end = Date.now() + 8000;
    while (Date.now() < this.nativeQuietUntil || document.querySelector('cart-items-component.cart-items-disabled')) {
      if (Date.now() > end) throw new Error('NATIVE_BUSY: Horizon meldet noch eine laufende Warenkorbaenderung.');
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }
  lock() {
    this.writing = true;
    // Keep the tooltip usable; freeze only quantity/forms and checkout during OUR writes.
    document.querySelectorAll('cart-items-component .cart-form, cart-items-component .cart__ctas').forEach(node => {
      if (!this.lockedNodes.has(node)) this.lockedNodes.set(node, node.inert);
      node.inert = true;
    });
  }
  unlock() {
    for (const [node, wasInert] of this.lockedNodes) if (node.isConnected) node.inert = wasInert;
    this.lockedNodes.clear();
    this.writing = false;
    document.querySelectorAll('cart-items-component .cart__ctas').forEach(node => {
      if (this.checkoutHold) {
        if (!node.hasAttribute('data-ag-checkout-hold')) node.dataset.agPreviousInert = String(node.inert);
        node.dataset.agCheckoutHold = '';
        node.inert = true;
      } else if (node.hasAttribute('data-ag-checkout-hold')) {
        node.inert = node.dataset.agPreviousInert === 'true';
        node.removeAttribute('data-ag-checkout-hold');
        node.removeAttribute('data-ag-previous-inert');
      }
    });
  }
  next(snapshot) {
    const own = managed(snapshot);
    const active = qualified(snapshot);
    if (this.intent) {
      const choice = this.intent;
      this.intent = null;
      const value = choice === 'decline' ? 'declined' : '';
      if (snapshot.giftState !== value) return { type: 'state', value };
    }
    if (!active || snapshot.giftState === 'declined') {
      if (snapshot.giftState === 'in_cart') return { type: 'state', value: '' };
      if (own.length) return { type: 'change', key: own[own.length - 1].key, quantity: 0 };
      return { type: 'idle' };
    }
    const wrongGift = own.find(item => item.variantId !== snapshot.variantId || item.subscription);
    if (wrongGift) return { type: 'change', key: wrongGift.key, quantity: 0 };
    if (own.length > 1) return { type: 'change', key: own[own.length - 1].key, quantity: 0 };
    if (own.length === 1 && own[0].quantity !== 1) return { type: 'change', key: own[0].key, quantity: 1 };
    const alreadyFree = snapshot.items.filter(item => !item.managed && item.variantId === snapshot.variantId && isFree(item));
    if (alreadyFree.length) {
      // Shopify may discount the customer's regular line instead. Do not delete that purchase.
      if (own.length) return { type: 'change', key: own[0].key, quantity: 0 };
      if (snapshot.giftState === 'in_cart') return { type: 'state', value: '' };
      return { type: 'idle', existingFree: totalQuantity(alreadyFree) };
    }
    if (own.length && !isFree(own[0])) {
      this.blocked = { signature: fingerprint(snapshot), code: 'GIFT_NOT_FREE' };
      return { type: 'change', key: own[0].key, quantity: 0 };
    }
    if (own.length) {
      if (snapshot.giftState !== 'in_cart') return { type: 'state', value: 'in_cart' };
      return { type: 'idle' };
    }
    // A missing item is not proof of a deliberate rejection. Only explicit actions set 'declined'.
    if (snapshot.giftState === 'in_cart') return { type: 'state', value: '' };
    if (this.blocked?.signature === fingerprint(snapshot)) return { type: 'idle' };
    return { type: 'add', variantId: snapshot.variantId };
  }
  async execute(operation) {
    if (operation.type === 'state') return this.post('cart/update.js', { attributes: { [AG_STATE]: operation.value } });
    if (operation.type === 'change') return this.post('cart/change.js', { id: operation.key, quantity: operation.quantity });
    if (operation.type === 'add') return this.post('cart/add.js', {
      items: [{ id: operation.variantId, quantity: 1, properties: { _free_gift: 'marcelpaa' } }],
    });
    throw new Error('UNKNOWN_OPERATION');
  }
  async transaction() {
    let wrote = false;
    let result;
    try {
      await this.waitForNativeIdle();
      let epoch = this.epoch;
      result = await this.read();
      if (epoch !== this.epoch) throw new AGStaleRead();
      this.snapshot = result.snapshot;
      if (this.blocked?.signature !== fingerprint(this.snapshot)) this.blocked = null;
      this.render();
      // Each operation uses the freshly returned snapshot, never cached line keys.
      for (let step = 0; step < 12; step++) {
        if (epoch !== this.epoch) {
          this.unlock();
          await this.waitForNativeIdle();
          epoch = this.epoch;
          result = await this.read(wrote);
          if (epoch !== this.epoch) throw new AGStaleRead();
        }
        this.snapshot = result.snapshot;
        const operation = this.next(this.snapshot);
        if (operation.type === 'idle') {
          if (wrote) await this.publish(result);
          this.checkoutHold = false;
          if (!this.blocked) this.lastError = null;
          this.render();
          return;
        }
        await this.waitForNativeIdle();
        if (epoch !== this.epoch) throw new AGStaleRead();
        this.lock();
        if (operation.type === 'add') this.setMessage('Dein Geschenk wird hinzugef\u00fcgt \u2026');
        wrote = true;
        result = await this.execute(operation);
      }
      throw new Error('NO_CONVERGENCE: Geschenkstatus wurde nach 12 Schritten nicht stabil.');
    } catch (error) {
      if (error instanceof AGStaleRead) {
        if (wrote) this.checkoutHold = true;
        this.queued = true;
        return;
      }
      this.lastError = { message: error.message || String(error), time: new Date().toISOString() };
      this.trace('ERROR', this.lastError);
      console.warn('[14Agency Free Gift v2]', error);
      // Only read after uncertain errors. Do NOT blindly repeat an add request.
      if (wrote) {
        try {
          result = await this.read(true);
          // Fail closed for an automatically added item with a nonzero price.
          // Always read each next key from the most recent successful response.
          for (let recoveryStep = 0; recoveryStep < 5; recoveryStep++) {
            const paidGift = managed(result.snapshot).find(item => !isFree(item));
            if (!paidGift) break;
            result = await this.post('cart/change.js', { id: paidGift.key, quantity: 0 });
          }
          this.snapshot = result.snapshot;
          this.checkoutHold = managed(this.snapshot).some(item => !isFree(item));
          await this.publish(result);
        } catch (recovery) {
          this.checkoutHold = true;
          this.trace('RECOVERY_FAILED', { message: recovery.message });
        }
      }
      if (this.snapshot) this.blocked = { signature: fingerprint(this.snapshot), code: 'API_ERROR' };
      this.render();
      this.setMessage('Dein Geschenk ist noch nicht best\u00e4tigt.');
      this.setNotice(this.checkoutHold ? 'Bitte den Warenkorb neu laden, bevor du zur Kasse gehst. Der Geschenkpreis konnte nicht best\u00e4tigt werden.' : 'Das Geschenk konnte nicht aktualisiert werden. Bitte erneut pr\u00fcfen.');
      document.querySelectorAll('[data-ag-gift-actions], [data-ag-gift-action="retry"]').forEach(node => { node.hidden = false; });
    } finally { this.unlock(); }
  }
  async publish(result) {
    const sections = {};
    for (const id of this.sectionIds()) {
      if (typeof result.sections?.[id] !== 'string') throw new Error(`UI_SECTION_MISSING: ${id}`);
      sections[id] = result.sections[id];
    }
    const snapshot = result.snapshot;
    // Exact event signature verified against the user's events.js and component-cart-items.js.
    // Reuse the complete Ajax cart response. add.js alone is not a full cart.
    const resource = result.resource || await this.fetchJSON(this.url('cart.js'));
    this.stats.publishes++;
    this.trace('PUBLISH');
    document.dispatchEvent(new this.events.CartUpdateEvent(resource, AG_SOURCE, {
      source: AG_SOURCE, itemCount: snapshot.itemCount, sections,
    }));
    // Native morphing may replace the teaser. Reapply this result, without new requests.
    queueMicrotask(() => this.render());
    requestAnimationFrame(() => this.render());
  }
  async run() {
    if (this.processing || this.abort.signal.aborted) { this.queued = true; return; }
    this.processing = true;
    this.stats.runs++;
    try {
      // Also coordinate cooperating copies in other same-origin browser tabs.
      if (navigator.locks?.request) await navigator.locks.request('14agency-free-gift-v2', () => this.transaction());
      else await this.transaction();
    } finally {
      this.processing = false;
      if (this.queued) { this.queued = false; this.schedule(150); }
    }
  }
  money(amount, currency) {
    if (!this.formatters.has(currency)) this.formatters.set(currency, new Intl.NumberFormat(
      document.documentElement.lang || 'de-DE', { style: 'currency', currency }));
    return this.formatters.get(currency).format(amount / 100);
  }
  setMessage(text) {
    document.querySelectorAll('[data-ag-message]').forEach(node => { node.textContent = text; });
  }
  setNotice(text) {
    document.querySelectorAll('[data-ag-notice]').forEach(node => { node.textContent = text; node.hidden = !text; });
  }
  decorateRows() {
    if (!this.snapshot) return;
    const keys = new Set(managed(this.snapshot).map(item => item.key));
    document.querySelectorAll('cart-items-component .cart-items__table-row[data-key]').forEach(row => {
      const own = keys.has(row.dataset.key);
      if (own) row.dataset.agManagedGift = 'true';
      else row.removeAttribute('data-ag-managed-gift');
      let label = row.querySelector('[data-ag-quantity-label]');
      if (!own) { label?.remove(); return; }
      if (!label) {
        const controls = row.querySelector('.cart-items__quantity-controls');
        if (!controls) return;
        label = document.createElement('span');
        label.dataset.agQuantityLabel = '';
        label.className = 'ag-free-gift__fixed-quantity';
        controls.prepend(label);
      }
      label.textContent = `${this.snapshot.items.find(item => item.key === row.dataset.key).quantity} Gratisgeschenk`;
    });
  }
  render() {
    const snapshot = this.snapshot;
    if (!snapshot) return;
    const own = managed(snapshot);
    const otherFree = snapshot.items.filter(item => !item.managed && item.variantId === snapshot.variantId && isFree(item));
    const giftOK = own.length === 1 && own[0].quantity === 1 && isFree(own[0]) && qualified(snapshot);
    const existingFree = totalQuantity(otherFree);
    const declined = snapshot.giftState === 'declined';
    const remaining = Math.max(0, snapshot.thresholdCents - snapshot.qualifiedAmount);
    const ratio = snapshot.thresholdCents > 0 ? Math.min(100, Math.max(0, snapshot.qualifiedAmount / snapshot.thresholdCents * 100)) : 0;
    let notice = '';
    if (this.lastError) notice = 'Das Geschenk konnte nicht aktualisiert werden. Bitte erneut pr\u00fcfen.';
    else if (this.blocked?.code === 'API_ERROR') notice = 'Die letzte Pr\u00fcfung ist fehlgeschlagen. Bitte erneut pr\u00fcfen.';
    else if (this.blocked?.code === 'GIFT_NOT_FREE') notice = 'Das Gratisgeschenk ist mit dem aktuellen Warenkorb nicht kombinierbar.';
    else if (!snapshot.currencySupported) notice = 'Diese Aktion ist nur f\u00fcr EUR und CHF konfiguriert.';
    else if (!snapshot.available) notice = 'Das Gratisgeschenk ist momentan nicht verf\u00fcgbar.';
    else if (existingFree > 1) notice = 'Mehrere Artikel werden kostenlos berechnet. Die Rabattregel muss gepr\u00fcft werden.';
    else if (snapshot.qualifiedAmount === 0 && snapshot.physicalQuantity > 0) notice = 'Diese Artikel z\u00e4hlen nicht zu den teilnahmeberechtigten Produkten.';
    for (const element of document.querySelectorAll('[data-ag-free-gift]')) {
      element.hidden = !snapshot.enabled || !snapshot.ready || (snapshot.physicalQuantity === 0 && own.length === 0);
      if (this.checkoutHold) notice = 'Bitte den Warenkorb neu laden. Der Geschenkpreis konnte nicht best\u00e4tigt werden.';
      element.dataset.agState = this.lastError ? 'error' : declined ? 'declined' : giftOK ? 'gifted' : 'progress';
      element.dataset.agQualifiedCents = String(snapshot.qualifiedAmount);
      element.classList.toggle('is-achieved', giftOK);
      element.classList.toggle('is-declined', declined);
      const fill = element.querySelector('[data-ag-fill]');
      const track = element.querySelector('[data-ag-track]');
      if (fill) fill.style.width = `${ratio.toFixed(2)}%`;
      if (track) track.setAttribute('aria-valuenow', String(Math.round(ratio)));
      const progress = element.querySelector('[data-ag-progress-wrap]');
      if (progress) progress.hidden = false;
      const message = element.querySelector('[data-ag-message]');
      if (message) {
        message.textContent = this.lastError || this.blocked ? 'Dein Geschenk ist noch nicht best\u00e4tigt.'
          : remaining > 0 ? (element.dataset.before || 'Nur noch [amount] bis zu deinem Gratisgeschenk!').replaceAll('[amount]', this.money(remaining, snapshot.currency))
          : declined ? 'Mindestwarenwert erreicht. Geschenk abgew\u00e4hlt.'
          : giftOK ? (element.dataset.after || 'Geschafft! Dein Gratisgeschenk ist dabei.')
          : existingFree === 1 ? 'Dein Geschenk-Rabatt ist bereits im Warenkorb.'
          : existingFree > 1 ? 'Geschenk-Rabatt wird gepr\u00fcft.'
          : !snapshot.available ? 'Mindestwarenwert erreicht.' : 'Dein Gratisgeschenk wird gepr\u00fcft \u2026';
      }
      const success = element.querySelector('[data-ag-success]');
      if (success) { success.hidden = true; success.textContent = ''; } // No duplicate success text.
      const actions = element.querySelector('[data-ag-gift-actions]');
      const decline = element.querySelector('[data-ag-gift-action="decline"]');
      const restore = element.querySelector('[data-ag-gift-action="restore"]');
      const retry = element.querySelector('[data-ag-gift-action="retry"]');
      if (decline) decline.hidden = !own.length || declined;
      if (restore) { restore.hidden = !declined; restore.disabled = !qualified(snapshot); }
      if (retry) retry.hidden = !this.lastError && !this.blocked;
      if (actions) actions.hidden = !own.length && !declined && !this.lastError && !this.blocked;
      const note = element.querySelector('[data-ag-notice]');
      if (note) { note.textContent = notice; note.hidden = !notice; }
    }
    this.decorateRows();
  }
}

async function startAGFreeGift() {
  if (document.readyState === 'loading') await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
  if (window.__marcelPaaFreeGift) {
    if (window.__marcelPaaFreeGift.version !== AG_VERSION) console.warn('[14Agency Free Gift] Alte Instanz aktiv: Seite vollstaendig neu laden.');
    return;
  }
  try {
    // UI above remains usable even if this import fails. Do not guess exports.
    const events = await import('@theme/events');
    if (!events.CartUpdateEvent || events.ThemeEvents?.cartUpdate !== 'cart:update') throw new Error('HORIZON_INTERFACE: events.js passt nicht zur gelieferten 3.2.1-Schnittstelle.');
    if (!window.__marcelPaaFreeGift) window.__marcelPaaFreeGift = new MarcelPaaFreeGift(events);
  } catch (error) {
    console.error('[14Agency Free Gift] Start fehlgeschlagen', error);
    document.querySelectorAll('[data-ag-message]').forEach(node => { node.textContent = 'Geschenkfunktion konnte nicht gestartet werden.'; });
  }
}
void startAGFreeGift();
